import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { dateKeyInTimeZone } from '@/lib/date-values';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import {
  AnalyticsReportShell,
  BreakdownTable,
  MetricCard,
  ReportDateRangeControl,
  ReportLineChart,
  ReportSection,
  ReportTeamFilter,
  ScorecardTable,
  computeDeltaPct,
  formatCurrency,
  formatInteger,
  formatPercent,
  useReportPeriod,
  type ScorecardColumn,
  type TeamFilterOption,
} from '@/components/organisms/analytics-reports';

import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { exportRowsAsXLSX } from '@/lib/export';
import { membershipEndpoints } from '@/lib/api/endpoints';
import { membershipKeys } from '@/lib/queryKeys';

export const Route = createFileRoute('/_dashboard/dashboard/memberships/reports')({
  component: MembershipReportsPage,
});

// ─── Types ──────────────────────────────────────────────────────────────────

interface OverviewData {
  totalActive: number;
  totalPending: number;
  totalPastDue: number;
  totalSuspended: number;
  totalCancelled: number;
  totalExpired: number;
  mrr: number;
  arr: number;
  avgTenureMonths: number;
  avgBillingAmount: number;
  totalCollected: number;
  totalSavingsGiven: number;
}

interface RevenueData {
  mrr: number;
  arr: number;
  totalCollected: number;
  avgRevenuePerMember: number;
  avgLifetimeValue: number;
}

interface ChurnData {
  totalCancelled: number;
  byReason: { reason: string; count: number }[];
  churnRate: number;
  saveRate: number;
}

interface SalesData {
  totalSold: number;
  bySource: { source: string; count: number }[];
  bySoldByRole: { role: string; count: number }[];
  avgTimeToActivateDays: number;
}

interface UtilizationData {
  avgUtilizationScore: number;
  underutilizedCount: number;
  totalEntitlementsUsed: number;
  totalEntitlementsAvailable: number;
  usageRate: number;
}

interface PlanPerformanceRow {
  planId: string;
  planName: string;
  activeCount: number;
  totalEver: number;
  retentionRate: number;
  avgUtilization: number;
  totalRevenue: number;
  avgRevenuePerMember: number;
  churnCount: number;
  churnRate: number;
}

interface ActivityWindow {
  newCount: number;
  activatedCount: number;
  canceledCount: number;
  expiredCount: number;
  conversionRate: number;
}
interface ActivityData {
  period: { from: string; to: string };
  current: ActivityWindow;
  previous: ActivityWindow;
  trend: { period: string; value: number }[];
  bySource: { source: string; count: number }[];
  bySoldByRole: { role: string; count: number }[];
  teamOptions: TeamFilterOption[];
}

const planColumns: ScorecardColumn<PlanPerformanceRow>[] = [
  { key: 'plan', header: 'Plan', type: 'text', value: (p) => p.planName },
  {
    key: 'active',
    header: 'Active',
    type: 'number',
    value: (p) => p.activeCount,
    total: (rows) => rows.reduce((s, r) => s + r.activeCount, 0),
  },
  { key: 'retention', header: 'Retention', type: 'percent', value: (p) => p.retentionRate },
  { key: 'util', header: 'Avg Util', type: 'percent', value: (p) => p.avgUtilization },
  {
    key: 'revenue',
    header: 'Revenue',
    type: 'currency',
    value: (p) => p.totalRevenue,
    total: (rows) => rows.reduce((s, r) => s + r.totalRevenue, 0),
  },
  { key: 'churn', header: 'Churn', type: 'percent', value: (p) => p.churnRate },
];

// ─── Page ───────────────────────────────────────────────────────────────────

function MembershipReportsPage() {
  const period = useReportPeriod('analytics.memberships.reportPeriod');
  const [ownerId, setOwnerId] = useState<string | undefined>(undefined);
  const activityParams = { from: period.from, to: period.to, ownerId };

  const activityQuery = useApiQuery<ActivityData>({
    queryKey: membershipKeys.report('activity', activityParams),
    queryFn: async () =>
      (
        await apiGet<{ data: ActivityData }>(
          operationsClient,
          membershipEndpoints.reportActivity(activityParams),
        )
      ).data,
    showErrorToast: false,
  });
  const activity = activityQuery.data;
  const curA = activity?.current;
  const prevA = activity?.previous;
  // The membership report endpoints wrap their payload in `{ data: ... }`;
  // unwrap here so the render reads the real fields directly.
  const overviewQuery = useApiQuery<OverviewData>({
    queryKey: membershipKeys.report('overview'),
    queryFn: async () =>
      (await apiGet<{ data: OverviewData }>(operationsClient, membershipEndpoints.reportOverview()))
        .data,
    showErrorToast: false,
  });

  const revenueQuery = useApiQuery<RevenueData>({
    queryKey: membershipKeys.report('revenue'),
    queryFn: async () =>
      (await apiGet<{ data: RevenueData }>(operationsClient, membershipEndpoints.reportRevenue()))
        .data,
    showErrorToast: false,
  });

  const churnQuery = useApiQuery<ChurnData>({
    queryKey: membershipKeys.report('churn'),
    queryFn: async () =>
      (await apiGet<{ data: ChurnData }>(operationsClient, membershipEndpoints.reportChurn())).data,
    showErrorToast: false,
  });

  const salesQuery = useApiQuery<SalesData>({
    queryKey: membershipKeys.report('sales'),
    queryFn: async () =>
      (await apiGet<{ data: SalesData }>(operationsClient, membershipEndpoints.reportSales())).data,
    showErrorToast: false,
  });

  const utilizationQuery = useApiQuery<UtilizationData>({
    queryKey: membershipKeys.report('utilization'),
    queryFn: async () =>
      (
        await apiGet<{ data: UtilizationData }>(
          operationsClient,
          membershipEndpoints.reportUtilization(),
        )
      ).data,
    showErrorToast: false,
  });

  const planQuery = useApiQuery<PlanPerformanceRow[]>({
    queryKey: membershipKeys.report('plan-performance'),
    queryFn: async () =>
      (
        await apiGet<{ data: { plans: PlanPerformanceRow[] } }>(
          operationsClient,
          membershipEndpoints.reportPlanPerformance(),
        )
      ).data.plans,
    showErrorToast: false,
  });

  const overview = overviewQuery.data;
  const revenue = revenueQuery.data;
  const churn = churnQuery.data;
  const sales = salesQuery.data;
  const utilization = utilizationQuery.data;
  const plans = planQuery.data ?? [];

  async function handleExport() {
    if (!plans.length) return;
    const rows = plans.map((p) => ({
      Plan: p.planName,
      Active: p.activeCount,
      'Total Ever': p.totalEver,
      Retention: formatPercent(p.retentionRate),
      'Avg Util': formatPercent(p.avgUtilization),
      Revenue: p.totalRevenue,
      Churn: formatPercent(p.churnRate),
    }));
    // Period bounds are UTC-snapped ISO instants; label the file with their UTC
    // calendar date (the same boundary the backend filters on).
    const fname = `memberships-report-${dateKeyInTimeZone(period.from, 'UTC')}_${dateKeyInTimeZone(period.to, 'UTC')}.xlsx`;
    await exportRowsAsXLSX(rows, fname, { currencyColumns: ['Revenue'] });
  }

  return (
    <AnalyticsReportShell
      title="Membership Reports"
      subtitle="Performance overview and analytics"
      backTo="/dashboard/memberships"
      onExport={handleExport}
      exportDisabled={!plans.length}
      actions={
        <>
          <ReportTeamFilter
            value={ownerId}
            onChange={setOwnerId}
            options={activity?.teamOptions ?? []}
            allLabel="All sold-by"
          />
          <ReportDateRangeControl period={period} />
        </>
      }
    >
      {/* Activity (date-ranged) */}
      <ReportSection title="Activity">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          <MetricCard
            title="New"
            value={curA ? formatInteger(curA.newCount) : '--'}
            isLoading={activityQuery.isLoading}
            deltaPct={computeDeltaPct(curA?.newCount, prevA?.newCount)}
          />
          <MetricCard
            title="Activated"
            value={curA ? formatInteger(curA.activatedCount) : '--'}
            isLoading={activityQuery.isLoading}
            deltaPct={computeDeltaPct(curA?.activatedCount, prevA?.activatedCount)}
          />
          <MetricCard
            title="Canceled"
            value={curA ? formatInteger(curA.canceledCount) : '--'}
            isLoading={activityQuery.isLoading}
            deltaPct={computeDeltaPct(curA?.canceledCount, prevA?.canceledCount)}
            deltaPositiveIsGood={false}
          />
          <MetricCard
            title="Expired"
            value={curA ? formatInteger(curA.expiredCount) : '--'}
            isLoading={activityQuery.isLoading}
            deltaPct={computeDeltaPct(curA?.expiredCount, prevA?.expiredCount)}
            deltaPositiveIsGood={false}
          />
          <MetricCard
            title="Conversion Rate"
            value={curA ? formatPercent(curA.conversionRate) : '--'}
            isLoading={activityQuery.isLoading}
            deltaPct={computeDeltaPct(curA?.conversionRate, prevA?.conversionRate)}
          />
        </div>
        <ReportLineChart
          title="New memberships by month"
          isLoading={activityQuery.isLoading}
          points={(activity?.trend ?? []).map((p) => ({ period: p.period, value: p.value }))}
        />
      </ReportSection>

      {/* Overview */}
      <ReportSection title="Overview">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Active Members"
            value={overview ? formatInteger(overview.totalActive) : '--'}
            isLoading={overviewQuery.isLoading}
          />
          <MetricCard
            title="Pending"
            value={overview ? formatInteger(overview.totalPending) : '--'}
            isLoading={overviewQuery.isLoading}
          />
          <MetricCard
            title="MRR"
            value={overview ? formatCurrency(overview.mrr) : '--'}
            isLoading={overviewQuery.isLoading}
          />
          <MetricCard
            title="ARR"
            value={overview ? formatCurrency(overview.arr) : '--'}
            isLoading={overviewQuery.isLoading}
          />
        </div>
      </ReportSection>

      {/* Revenue */}
      <ReportSection title="Revenue">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="ARR"
            value={revenue ? formatCurrency(revenue.arr) : '--'}
            isLoading={revenueQuery.isLoading}
          />
          <MetricCard
            title="Total Collected"
            value={revenue ? formatCurrency(revenue.totalCollected) : '--'}
            isLoading={revenueQuery.isLoading}
          />
          <MetricCard
            title="Avg Revenue Per Member"
            value={revenue ? formatCurrency(revenue.avgRevenuePerMember) : '--'}
            isLoading={revenueQuery.isLoading}
          />
          <MetricCard
            title="Avg Lifetime Value"
            value={revenue ? formatCurrency(revenue.avgLifetimeValue) : '--'}
            isLoading={revenueQuery.isLoading}
          />
        </div>
      </ReportSection>

      {/* Churn */}
      <ReportSection title="Churn">
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Cancellation Reasons</CardTitle>
            </CardHeader>
            <CardContent>
              <BreakdownTable
                rows={churn?.byReason ?? []}
                isLoading={churnQuery.isLoading}
                emptyMessage="No cancellation data available"
                skeletonRows={4}
                columns={[
                  { key: 'reason', header: 'Reason', align: 'left', render: (r) => r.reason },
                  { key: 'count', header: 'Count', align: 'right', render: (r) => r.count },
                ]}
              />
            </CardContent>
          </Card>
          <div className="grid gap-4 sm:grid-cols-2">
            <MetricCard
              title="Save Rate"
              value={churn ? formatPercent(churn.saveRate) : '--'}
              isLoading={churnQuery.isLoading}
            />
            <MetricCard
              title="Churn Rate"
              value={churn ? formatPercent(churn.churnRate) : '--'}
              isLoading={churnQuery.isLoading}
            />
          </div>
        </div>
      </ReportSection>

      {/* Sales */}
      <ReportSection title="Sales">
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Sold by Source</CardTitle>
            </CardHeader>
            <CardContent>
              <BreakdownTable
                rows={sales?.bySource ?? []}
                isLoading={salesQuery.isLoading}
                emptyMessage="No sales data available"
                skeletonRows={3}
                columns={[
                  { key: 'source', header: 'Source', align: 'left', render: (s) => s.source },
                  { key: 'count', header: 'Count', align: 'right', render: (s) => s.count },
                ]}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Sold by Role</CardTitle>
            </CardHeader>
            <CardContent>
              <BreakdownTable
                rows={sales?.bySoldByRole ?? []}
                isLoading={salesQuery.isLoading}
                emptyMessage="No role data available"
                skeletonRows={3}
                columns={[
                  { key: 'role', header: 'Role', align: 'left', render: (r) => r.role },
                  { key: 'count', header: 'Count', align: 'right', render: (r) => r.count },
                ]}
              />
            </CardContent>
          </Card>
        </div>
      </ReportSection>

      {/* Utilization */}
      <ReportSection title="Utilization">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <MetricCard
            title="Underutilized Members"
            value={utilization ? formatInteger(utilization.underutilizedCount) : '--'}
            isLoading={utilizationQuery.isLoading}
          />
          <MetricCard
            title="Entitlements Used / Available"
            value={
              utilization
                ? `${formatInteger(utilization.totalEntitlementsUsed)} / ${formatInteger(
                    utilization.totalEntitlementsAvailable,
                  )}`
                : '--'
            }
            isLoading={utilizationQuery.isLoading}
          />
          <MetricCard
            title="Usage Rate"
            value={utilization ? formatPercent(utilization.usageRate) : '--'}
            isLoading={utilizationQuery.isLoading}
          />
        </div>
      </ReportSection>

      {/* Plan Performance */}
      <ReportSection title="Plan Performance">
        <Card>
          <CardContent className="pt-6">
            <ScorecardTable<PlanPerformanceRow>
              rows={plans}
              isLoading={planQuery.isLoading}
              emptyMessage="No plan performance data available"
              skeletonRows={5}
              initialSortKey="active"
              columns={planColumns}
            />
          </CardContent>
        </Card>
      </ReportSection>
    </AnalyticsReportShell>
  );
}
